import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, ChevronDown, ChevronRight, FileDown, FileText, FileX2, Loader2, Pencil, Send, Terminal, Trash2 } from "lucide-react"
import { AppLayout } from "../../components/layout/AppLayout"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { EmissaoStatusBadge } from "../../features/invoices/emissaoStatus"

type TributoLinha = {
  codigo: string
  nome: string
  aliquota_percentual: number
  valor: number
  retido: boolean
}

type InvoiceShowProps = {
  invoice: {
    id: number
    codigo_sap: string
    cr: string
    pedido_compras?: string
    data_vencimento?: string
    company_name: string
    client_name: string
    municipio: string
    ibge_codigo_municipio_prestacao?: string | null
    rps_situacao_letra?: string | null
    rps_situacao_descricao?: string | null
    rps_situacao_codigo_omie?: string | null
    aliquota_iss_layout?: number | null
    medicao: number
    periodo_inicio: string
    periodo_fim: string
    valor: number
    tributos: number
    total_liquido: number
    tributos_origem?: string
    emissao_status: string
    omie_id_nf?: number | null
    omie_id_os?: number | null
    omie_numero_nfs?: string | null
    omie_numero_rps?: string | null
    omie_nfse_url?: string | null
    omie_nfse_xml_url?: string | null
    omie_nfse_cancelamento_xml_url?: string | null
    omie_servico_label?: string
    omie_conta_label?: string
    payment_term_label?: string
    omie_n_cod_os?: number | null
    omie_emissao_erro?: string | null
    omie_ultima_emissao_em?: string | null
    pode_enviar_omie?: boolean
    created_at: string
    tributos_detalhe?: {
      linhas: TributoLinha[]
      total: number
      sem_config?: boolean
    }
    omie_api_logs?: OmieApiLogRow[]
  }
}

type OmieApiLogRow = {
  id: number
  operation: string
  http_status: number | null
  duration_ms: number | null
  error_summary?: string | null
  created_at: string | null
  request_body: string
  response_body: string
}

function money(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value)
}

function date(value: string) {
  if (!value) return "-"
  return new Date(`${value}T00:00:00`).toLocaleDateString("pt-BR")
}

function pct(value: number) {
  return `${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`
}

function formatJsonIfPossible(raw: string) {
  const t = raw.trim()
  if (!t.startsWith("{") && !t.startsWith("[")) return raw
  try {
    return JSON.stringify(JSON.parse(t), null, 2)
  } catch {
    return raw
  }
}

export default function InvoiceShow({ invoice }: InvoiceShowProps) {
  const [mostrarLogsOmie, setMostrarLogsOmie] = useState(false)
  const [expandedOmieLogId, setExpandedOmieLogId] = useState<number | null>(null)
  const [loadingDoc, setLoadingDoc] = useState<"pdf" | "xml" | null>(null)
  const omieLogs = invoice.omie_api_logs ?? []
  const podeBaixarDocumentosNfse = (invoice.omie_id_nf ?? 0) > 0
  const isTxtImport = invoice.tributos_origem === "txt_importacao"
  const tributosDetalhe = invoice.tributos_detalhe ?? {
    linhas: [],
    total: invoice.tributos,
    sem_config: true
  }

  function handleDelete() {
    const confirmed = window.confirm(`Tem certeza que deseja excluir a nota fiscal ${invoice.codigo_sap}?`)
    if (confirmed) router.delete(`/notas-fiscais/${invoice.id}`)
  }

  function handleEnviarOmie() {
    if (invoice.pode_enviar_omie !== true) return
    const ok = window.confirm("Enfileirar envio da ordem de serviço (IncluirOS) para a Omie?")
    if (!ok) return
    router.post(`/notas-fiscais/${invoice.id}/enviar-omie`)
  }

  async function handleAbrirDocumentoNfse(tipo: "pdf" | "xml") {
    if (!podeBaixarDocumentosNfse || loadingDoc) return

    setLoadingDoc(tipo)
    try {
      const res = await fetch(`/notas-fiscais/${invoice.id}/documentos-nfse?tipo=${tipo}`, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
        }
      })
      const data = (await res.json()) as { url?: string; error?: string }
      if (!res.ok || !data.url) {
        throw new Error(data.error || "Não foi possível obter o documento na Omie.")
      }
      window.open(data.url, "_blank", "noopener,noreferrer")
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erro ao obter documento."
      window.alert(message)
    } finally {
      setLoadingDoc(null)
    }
  }

  return (
    <>
      <Head title={`Nota Fiscal ${invoice.codigo_sap}`} />
      <AppLayout>
        <div className="mx-auto w-full max-w-6xl space-y-6 p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" asChild className="border-slate-700 bg-transparent">
                <Link href="/notas-fiscais">
                  <ArrowLeft className="h-4 w-4" />
                </Link>
              </Button>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold text-white">{invoice.codigo_sap}</h1>
                  <EmissaoStatusBadge status={invoice.emissao_status} />
                </div>
                <p className="text-slate-400">{invoice.client_name}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {invoice.omie_nfse_url && (
                <Button asChild variant="outline" className="border-sky-700 bg-sky-950/20 text-sky-200 hover:bg-sky-900/30">
                  <a href={invoice.omie_nfse_url} target="_blank" rel="noreferrer">
                    <FileText className="mr-2 h-4 w-4" />
                    Nota Fiscal
                  </a>
                </Button>
              )}
              {podeBaixarDocumentosNfse && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-rose-700 bg-rose-950/20 text-rose-200 hover:bg-rose-900/30"
                    disabled={loadingDoc !== null}
                    onClick={() => handleAbrirDocumentoNfse("pdf")}
                  >
                    {loadingDoc === "pdf" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <FileDown className="mr-2 h-4 w-4" />
                    )}
                    PDF
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-indigo-700 bg-indigo-950/20 text-indigo-200 hover:bg-indigo-900/30"
                    disabled={loadingDoc !== null}
                    onClick={() => handleAbrirDocumentoNfse("xml")}
                  >
                    {loadingDoc === "xml" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <FileX2 className="mr-2 h-4 w-4" />
                    )}
                    XML
                  </Button>
                </>
              )}
              {invoice.omie_nfse_cancelamento_xml_url && (
                <Button asChild variant="outline" className="border-amber-700 bg-amber-950/20 text-amber-200 hover:bg-amber-900/30">
                  <a href={invoice.omie_nfse_cancelamento_xml_url} target="_blank" rel="noreferrer">
                    <FileX2 className="mr-2 h-4 w-4" />
                    XML cancelamento
                  </a>
                </Button>
              )}
              {invoice.pode_enviar_omie === true && (
                <Button
                  type="button"
                  className="border-emerald-700 bg-emerald-950/40 text-emerald-200 hover:bg-emerald-900/30"
                  onClick={handleEnviarOmie}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Enviar à Omie
                </Button>
              )}
              <Button asChild variant="outline" className="border-slate-700 bg-transparent">
                <Link href={`/notas-fiscais/${invoice.id}/editar`}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Editar
                </Link>
              </Button>
              <Button variant="outline" className="border-rose-700 text-rose-400 hover:bg-rose-900/20" onClick={handleDelete}>
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir
              </Button>
            </div>
          </div>

          <Card className="border-slate-800 bg-slate-900">
            <CardHeader><CardTitle>Dados da Nota</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <div><p className="text-xs text-slate-400">Código SAP</p><p>{invoice.codigo_sap}</p></div>
              <div><p className="text-xs text-slate-400">CR</p><p>{invoice.cr}</p></div>
              {invoice.pedido_compras && <div><p className="text-xs text-slate-400">Pedido de Compras</p><p>{invoice.pedido_compras}</p></div>}
              {invoice.data_vencimento && <div><p className="text-xs text-slate-400">Vencimento</p><p>{new Date(invoice.data_vencimento + "T00:00:00").toLocaleDateString("pt-BR")}</p></div>}
              <div><p className="text-xs text-slate-400">Empresa</p><p>{invoice.company_name}</p></div>
              <div><p className="text-xs text-slate-400">Cliente</p><p>{invoice.client_name}</p></div>
              <div><p className="text-xs text-slate-400">Município</p><p>{invoice.municipio}</p></div>
              {invoice.ibge_codigo_municipio_prestacao && (
                <div>
                  <p className="text-xs text-slate-400">IBGE (prestação)</p>
                  <p>{invoice.ibge_codigo_municipio_prestacao}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-slate-400">Situação do RPS (TXT)</p>
                {invoice.rps_situacao_letra ? (
                  <div className="space-y-0.5">
                    <p>
                      <span className="font-mono text-lg font-semibold text-amber-200">{invoice.rps_situacao_letra}</span>
                      {invoice.rps_situacao_descricao && (
                        <span className="text-slate-300"> — {invoice.rps_situacao_descricao}</span>
                      )}
                    </p>
                    {invoice.rps_situacao_codigo_omie && (
                      <p className="text-xs text-slate-500">cTribServ Omie: {invoice.rps_situacao_codigo_omie}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500">—</p>
                )}
              </div>
              {invoice.aliquota_iss_layout != null && invoice.aliquota_iss_layout > 0 && (
                <div>
                  <p className="text-xs text-slate-400">Alíquota ISS (layout TXT)</p>
                  <p className="tabular-nums">{pct(invoice.aliquota_iss_layout)}</p>
                </div>
              )}
              <div><p className="text-xs text-slate-400">N. Nota Fiscal</p><p>{invoice.omie_numero_nfs || "—"}</p></div>
              <div><p className="text-xs text-slate-400">N. RPS</p><p>{invoice.omie_numero_rps || "—"}</p></div>
              <div><p className="text-xs text-slate-400">ID NF (Omie)</p><p>{invoice.omie_id_nf ?? "—"}</p></div>
              <div><p className="text-xs text-slate-400">ID OS (Omie)</p><p>{invoice.omie_id_os ?? invoice.omie_n_cod_os ?? "—"}</p></div>
              <div className="space-y-1">
                <p className="text-xs text-slate-400">Status de emissão</p>
                <EmissaoStatusBadge status={invoice.emissao_status} />
              </div>
            </CardContent>
          </Card>

          {invoice.emissao_status === "enviada_para_emitir" && (
            <div className="rounded-lg border border-sky-800/80 bg-sky-950/30 px-4 py-3 text-sm text-sky-200">
              Envio à Omie em processamento em segundo plano. Atualize a página em alguns segundos.
            </div>
          )}

          {invoice.emissao_status === "erro_emissao" && invoice.omie_emissao_erro && (
            <div className="rounded-lg border border-rose-800/80 bg-rose-950/20 px-4 py-3 text-sm text-rose-200">
              <p className="font-medium text-rose-300">Falha ao enviar à Omie</p>
              <p className="mt-1 whitespace-pre-wrap text-rose-100/90">{invoice.omie_emissao_erro}</p>
            </div>
          )}

          {(invoice.omie_servico_label ||
            invoice.omie_conta_label ||
            invoice.payment_term_label ||
            invoice.omie_n_cod_os != null ||
            invoice.omie_ultima_emissao_em) && (
            <Card className="border-slate-800 bg-slate-900">
              <CardHeader>
                <CardTitle>Omie (emissão)</CardTitle>
                <p className="text-sm font-normal text-slate-400">Serviço, conta e retorno da API IncluirOS.</p>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="text-xs text-slate-400">Serviço (nCodServico)</p>
                  <p className="text-sm text-slate-200">{invoice.omie_servico_label || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Conta corrente (nCodCC)</p>
                  <p className="text-sm text-slate-200">{invoice.omie_conta_label || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Condição de pagamento</p>
                  <p className="text-sm text-slate-200">{invoice.payment_term_label || "—"}</p>
                </div>
                {invoice.omie_n_cod_os != null && invoice.omie_n_cod_os > 0 && (
                  <div>
                    <p className="text-xs text-slate-400">Ordem de serviço Omie (nCodOS)</p>
                    <p className="font-mono text-sm text-emerald-300">{invoice.omie_n_cod_os}</p>
                  </div>
                )}
                {invoice.omie_ultima_emissao_em && (
                  <div>
                    <p className="text-xs text-slate-400">Última tentativa de envio</p>
                    <p className="text-sm text-slate-200">
                      {new Date(invoice.omie_ultima_emissao_em).toLocaleString("pt-BR")}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-800 bg-slate-900">
            <CardHeader><CardTitle>Medição e Período</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div><p className="text-xs text-slate-400">Medição</p><p>{invoice.medicao}</p></div>
              <div><p className="text-xs text-slate-400">Período início</p><p>{date(invoice.periodo_inicio)}</p></div>
              <div><p className="text-xs text-slate-400">Período fim</p><p>{date(invoice.periodo_fim)}</p></div>
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900">
            <CardHeader>
              <CardTitle>Valores</CardTitle>
              <p className="text-sm font-normal text-slate-400">
                {isTxtImport
                  ? "Valor da nota e tributos importados do arquivo TXT."
                  : "Valor da nota e tributos calculados pela configuração fiscal."}
              </p>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:max-w-2xl">
                <div><p className="text-xs text-slate-400">Valor</p><p>{money(invoice.valor)}</p></div>
                <div><p className="text-xs text-slate-400">Tributos</p><p>{money(invoice.tributos)}</p></div>
                <div className="md:col-span-1">
                  <p className="text-xs text-slate-400">Total líquido</p>
                  <p className="font-semibold text-emerald-400">{money(invoice.total_liquido)}</p>
                  <p className="mt-0.5 text-xs text-slate-500">Valor - tributos</p>
                </div>
              </div>

              <div className="border-t border-slate-800 pt-6">
                <h3 className="text-sm font-semibold text-slate-200">Detalhamento dos tributos</h3>
                <p className="mt-1 text-xs text-slate-500">
                  {isTxtImport
                    ? "Tributos importados do TXT (referência: linha do arquivo de origem)."
                    : `Configuração fiscal do cliente — ${invoice.municipio || "—"} (base: valor da nota).`}
                </p>
                <div className="mt-4">
                  {tributosDetalhe.sem_config ? (
                    <p className="text-sm text-amber-500/90">
                      Não foi possível localizar a configuração fiscal deste município; o total de tributos salvo na nota é{" "}
                      <span className="font-medium text-slate-200">{money(tributosDetalhe.total)}</span>.
                    </p>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-slate-800">
                      <table className="w-full min-w-[520px] text-left text-sm">
                        <thead>
                          <tr className="border-b border-slate-800 bg-slate-950/80 text-xs uppercase tracking-wide text-slate-400">
                            <th className="px-4 py-3 font-medium">Imposto</th>
                            <th className="px-4 py-3 font-medium">Alíquota</th>
                            <th className="px-4 py-3 font-medium">Valor</th>
                            <th className="px-4 py-3 font-medium">Retido na fonte</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                          {tributosDetalhe.linhas.map((linha) => (
                            <tr key={linha.codigo} className="text-slate-200">
                              <td className="px-4 py-3">{linha.nome}</td>
                              <td className="px-4 py-3 tabular-nums text-slate-300">{pct(linha.aliquota_percentual)}</td>
                              <td className="px-4 py-3 tabular-nums font-medium">{money(linha.valor)}</td>
                              <td className="px-4 py-3">
                                {linha.retido ? <span className="text-emerald-400">Sim</span> : <span className="text-slate-500">Não</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-slate-700 bg-slate-950/50 font-medium text-slate-100">
                            <td className="px-4 py-3" colSpan={2}>
                              Total de tributos
                            </td>
                            <td className="px-4 py-3 tabular-nums">{money(tributosDetalhe.total)}</td>
                            <td className="px-4 py-3" />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4 border-t border-slate-800 pt-6">
            <Button
              type="button"
              variant="outline"
              className="border-slate-600 bg-slate-950/40 text-slate-300 hover:bg-slate-900/80"
              onClick={() => {
                if (mostrarLogsOmie) {
                  setExpandedOmieLogId(null)
                  setMostrarLogsOmie(false)
                } else {
                  setMostrarLogsOmie(true)
                }
              }}
            >
              <Terminal className="mr-2 h-4 w-4" />
              {mostrarLogsOmie ? "Ocultar logs técnicos Omie" : "Mostrar logs técnicos Omie"}
              {omieLogs.length > 0 && (
                <span className="ml-2 rounded-md bg-slate-800 px-1.5 py-0.5 font-mono text-xs text-slate-400">
                  {omieLogs.length}
                </span>
              )}
            </Button>

            {mostrarLogsOmie && (
              <Card className="border-slate-800 bg-slate-900">
                <CardHeader>
                  <CardTitle>Histórico de chamadas à Omie</CardTitle>
                  <p className="text-sm font-normal text-slate-400">
                    Request e response HTTP de cada chamada vinculada a esta nota (últimas 30). Útil para diagnosticar erro
                    500 ou falhas de API.
                  </p>
                </CardHeader>
                <CardContent>
                  {omieLogs.length === 0 ? (
                    <p className="text-sm text-slate-500">Nenhuma chamada registrada ainda para esta nota.</p>
                  ) : (
                    <div className="space-y-2">
                      {omieLogs.map((row) => {
                        const open = expandedOmieLogId === row.id
                        const httpLabel =
                          row.http_status == null ? "—" : String(row.http_status)
                        const dur =
                          row.duration_ms == null ? "—" : `${row.duration_ms} ms`
                        const when = row.created_at
                          ? new Date(row.created_at).toLocaleString("pt-BR")
                          : "—"
                        return (
                          <div
                            key={row.id}
                            className="overflow-hidden rounded-lg border border-slate-800 bg-slate-950/50"
                          >
                            <button
                              type="button"
                              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-slate-900/80"
                              onClick={() => setExpandedOmieLogId(open ? null : row.id)}
                            >
                              {open ? (
                                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
                              ) : (
                                <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
                              )}
                              <span className="font-mono text-xs text-sky-300">{row.operation}</span>
                              <span className="text-slate-500">HTTP {httpLabel}</span>
                              <span className="text-slate-500">{dur}</span>
                              <span className="ml-auto shrink-0 text-xs text-slate-500">{when}</span>
                            </button>
                            {row.error_summary && (
                              <p className="border-t border-slate-800/80 px-3 py-2 text-xs text-rose-300/95">
                                {row.error_summary}
                              </p>
                            )}
                            {open && (
                              <div className="border-t border-slate-800 space-y-3 p-3">
                                <div>
                                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                                    Request (secret mascarado)
                                  </p>
                                  <pre className="max-h-64 overflow-auto rounded-md border border-slate-800 bg-slate-950 p-3 text-xs text-slate-300 whitespace-pre-wrap break-all">
                                    {formatJsonIfPossible(row.request_body)}
                                  </pre>
                                </div>
                                <div>
                                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                                    Response
                                  </p>
                                  <pre className="max-h-64 overflow-auto rounded-md border border-slate-800 bg-slate-950 p-3 text-xs text-slate-300 whitespace-pre-wrap break-all">
                                    {formatJsonIfPossible(row.response_body)}
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </AppLayout>
    </>
  )
}
